/**
 * SchedaTraduzioni — i testi del questionario nelle altre lingue, campo per campo, col testo
 * sorgente accanto.
 *
 * Regola dei testi multilingua del gestionale: la lingua sorgente non cambia, le altre stanno in
 * `traduzioni[lingua]` indicizzate per CODICE; un testo che manca si legge nella sorgente (la
 * pagina pubblica non resta mai vuota). Le mancanti si contano con `questionarioInLingua`, la
 * stessa funzione che usa il server: il numero qui è quello che vedrà chi compila.
 * Gli indirizzi (informativa, collegamenti) si possono cambiare per lingua ma non contano come
 * traduzioni mancanti.
 */

import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { normalizzaQuestionario, questionarioInLingua } from '@shared/questionari';
import { tokens } from '../../../theme';
import { NOMI_LINGUA, nomeLingua } from '../labels';
import { questionarioDaBozza, type Bozza } from './bozza';
import { leggiVoce, scriviVoce, vociDelQuestionario, type VoceTraduzione } from './vociTraduzione';

interface Props {
  bozza: Bozza;
  onChange: (patch: Partial<Bozza>) => void;
}

export const SchedaTraduzioni: React.FC<Props> = ({ bozza, onChange }) => {
  const lingue = Object.keys(bozza.traduzioni).filter((l) => l !== bozza.lingua_sorgente).sort();
  const [scelta, setScelta] = useState<string>(lingue[0] ?? '');
  const [nuovaLingua, setNuovaLingua] = useState('');
  const [soloMancanti, setSoloMancanti] = useState(false);
  const [confermaRimozione, setConfermaRimozione] = useState(false);

  // Il questionario come lo vedrà il server, ricostruito dalla bozza a ogni modifica.
  const questionario = useMemo(() => normalizzaQuestionario(questionarioDaBozza(bozza)), [bozza]);
  const voci = useMemo(() => vociDelQuestionario(questionario), [questionario]);
  const mancantiPerLingua = useMemo(() => {
    const out: Record<string, Set<string>> = {};
    lingue.forEach((l) => { out[l] = new Set(questionarioInLingua(questionario, l).mancanti); });
    return out;
  }, [questionario, lingue.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const linguaAttiva = lingue.includes(scelta) ? scelta : lingue[0] ?? '';
  const proposte = Object.keys(NOMI_LINGUA).filter((l) => l !== bozza.lingua_sorgente && !lingue.includes(l));

  const aggiungiLingua = (codice: string) => {
    const l = codice.trim().toLowerCase();
    if (!/^[a-z]{2}$/.test(l) || l === bozza.lingua_sorgente || lingue.includes(l)) return;
    onChange({ traduzioni: { ...bozza.traduzioni, [l]: {} } });
    setScelta(l);
    setNuovaLingua('');
  };

  const rimuoviLingua = () => {
    const { [linguaAttiva]: _tolta, ...resto } = bozza.traduzioni;
    void _tolta;
    onChange({ traduzioni: resto });
    setConfermaRimozione(false);
    setScelta('');
  };

  const scrivi = (voce: VoceTraduzione, valore: string) => {
    onChange({
      traduzioni: { ...bozza.traduzioni, [linguaAttiva]: scriviVoce(bozza.traduzioni[linguaAttiva], voce.segmenti, valore) },
    });
  };

  const mancanti = linguaAttiva ? mancantiPerLingua[linguaAttiva] ?? new Set<string>() : new Set<string>();
  const vociVisibili = voci.filter((v) => !soloMancanti || mancanti.has(v.percorso));
  const gruppi: Array<{ gruppo: string; voci: VoceTraduzione[] }> = [];
  vociVisibili.forEach((v) => {
    const ultimo = gruppi[gruppi.length - 1];
    if (ultimo && ultimo.gruppo === v.gruppo) ultimo.voci.push(v);
    else gruppi.push({ gruppo: v.gruppo, voci: [v] });
  });

  return (
    <Stack spacing={2}>
      <Typography variant="body2" sx={{ color: tokens.color.ash }}>
        I testi scritti nelle altre schede sono in {nomeLingua(bozza.lingua_sorgente).toLowerCase()}. Qui si traducono per chi apre la
        pagina in un'altra lingua; un testo che manca si legge in {nomeLingua(bozza.lingua_sorgente).toLowerCase()}. I codici non si
        traducono mai: le risposte in tutte le lingue finiscono nelle stesse statistiche.
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {lingue.map((l) => {
          const n = mancantiPerLingua[l]?.size ?? 0;
          return (
            <Chip
              key={l}
              label={n > 0 ? `${nomeLingua(l)} · ne mancano ${n}` : `${nomeLingua(l)} · completa`}
              color={l === linguaAttiva ? 'primary' : 'default'}
              variant={l === linguaAttiva ? 'filled' : 'outlined'}
              onClick={() => { setScelta(l); setConfermaRimozione(false); }}
            />
          );
        })}
        <TextField
          select
          size="small"
          label="Aggiungi lingua"
          value=""
          onChange={(e) => aggiungiLingua(e.target.value)}
          sx={{ minWidth: 170 }}
          disabled={proposte.length === 0}
        >
          {proposte.map((l) => <MenuItem key={l} value={l}>{nomeLingua(l)}</MenuItem>)}
        </TextField>
        <TextField
          size="small"
          label="Altro codice"
          placeholder="es. pt"
          value={nuovaLingua}
          onChange={(e) => setNuovaLingua(e.target.value.toLowerCase().slice(0, 2))}
          sx={{ width: 120 }}
        />
        <Button size="small" startIcon={<Add />} onClick={() => aggiungiLingua(nuovaLingua)} disabled={!/^[a-z]{2}$/.test(nuovaLingua)}>
          Aggiungi
        </Button>
      </Stack>

      {!linguaAttiva ? (
        <Alert severity="info">Nessuna traduzione: aggiungi una lingua per cominciare.</Alert>
      ) : (
        <>
          <Stack direction="row" alignItems="center" spacing={2}>
            <FormControlLabel
              control={<Switch size="small" checked={soloMancanti} onChange={(e) => setSoloMancanti(e.target.checked)} />}
              label={<Typography variant="body2">Solo i testi che mancano</Typography>}
            />
            <Box sx={{ flex: 1 }} />
            {confermaRimozione ? (
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={() => setConfermaRimozione(false)}>Annulla</Button>
                <Button size="small" color="error" variant="contained" onClick={rimuoviLingua}>
                  Togli tutti i testi in {nomeLingua(linguaAttiva).toLowerCase()}
                </Button>
              </Stack>
            ) : (
              <Button size="small" color="error" startIcon={<DeleteOutline />} onClick={() => setConfermaRimozione(true)}>
                Togli la lingua
              </Button>
            )}
          </Stack>

          {gruppi.length === 0 && (
            <Typography variant="body2" sx={{ color: tokens.color.mist }}>
              {soloMancanti ? 'Non manca niente in questa lingua.' : 'Il questionario non ha ancora testi da tradurre.'}
            </Typography>
          )}

          {gruppi.map(({ gruppo, voci: vociGruppo }) => (
            <Box key={gruppo}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: tokens.color.graphite }}>{gruppo}</Typography>
              <Stack spacing={1.25}>
                {vociGruppo.map((v) => {
                  const valore = leggiVoce(bozza.traduzioni[linguaAttiva], v.segmenti);
                  const manca = mancanti.has(v.percorso);
                  return (
                    <Stack key={v.percorso} direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-start' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: tokens.color.ash, fontWeight: 600 }}>
                          {v.cosa}{v.riferimento ? ' (indirizzo: facoltativo)' : ''}
                        </Typography>
                        <Typography variant="body2" sx={{ color: tokens.color.ash, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                          {v.sorgente}
                        </Typography>
                      </Box>
                      <TextField
                        size="small"
                        fullWidth
                        multiline={v.lungo}
                        minRows={v.lungo ? 2 : undefined}
                        value={valore}
                        placeholder={v.riferimento ? 'Vuoto = lo stesso indirizzo' : `In ${nomeLingua(linguaAttiva).toLowerCase()}`}
                        onChange={(e) => scrivi(v, e.target.value)}
                        sx={{ flex: 1, '& .MuiOutlinedInput-root': manca ? { bgcolor: '#FFFBEB' } : undefined }}
                        inputProps={{ 'aria-label': `${v.gruppo} · ${v.cosa} in ${nomeLingua(linguaAttiva)}` }}
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </>
      )}
    </Stack>
  );
};
